// 全螢幕特效層 — 跨視圖的飛行/過場演出（螢幕座標，疊在 HUD 之上、彈窗之下）：
//   flyBullet：Scatter 收集／獎勵發放子彈（ani_fly "animation" loop），直線飛行、速度 2300 設計px/s、
//     Ease InQuad、彈頭朝行進方向旋轉（對照 Unity GamePlayUI.SpawnBullet + BulletView）。
//   playLevelTrans：免費遊戲等級過場字卡（ani_trans lv_1_L/lv_2_L/lv_3_L/lv_max，配 se_pk_{level}，
//     對照 Unity GamePlayUIMediator.ShowLevel）。
// 骨骼不可用時 fallback：子彈用金色圓點、過場直接略過（音效仍由 AudioManager 播）。

import { Container, Graphics } from 'pixi.js'
import type { Orientation, Resizable } from '../../framework/render/design'
import { delay } from '../../framework/core/async'
import {
  animationDuration,
  BULLET_SPEED_DESIGN,
  createEffectSpine,
  EFFECT_ANIM,
  hasAnimation,
  loadDeferredEffectSpines,
} from '../assets/EffectSpines'
import { killTweensOf, tweenTo } from './tween'

/** Unity 設計高（速度/縮放換算基準）。 */
const DESIGN_HEIGHT = 1080

export interface Point {
  readonly x: number
  readonly y: number
}

export class FxLayerView extends Container implements Resizable {
  private screenH = DESIGN_HEIGHT
  private screenW = 0
  private orientation: Orientation = 'landscape'

  constructor() {
    super()
    this.eventMode = 'none'
  }

  private get designScale(): number {
    return this.screenH / DESIGN_HEIGHT
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.screenW = width
    this.screenH = height
    this.orientation = orientation
    // 進行中的飛行以舊座標為準即可（下一發自然用新版面）；resize 不硬中斷。
  }

  /**
   * 子彈自 from 直線飛到 to（螢幕座標）並在抵達時銷毀。速度 2300 設計px/s（依螢幕縮放換算）、
   * Ease InQuad、彈頭持續朝行進方向。骨骼不可用時退回金色圓點。
   */
  async flyBullet(from: Point, to: Point): Promise<void> {
    // ani_fly 屬非首屏資產（見 main.ts 背景載入）；播放前確保就緒，未就緒則退回金色圓點。
    await loadDeferredEffectSpines()
    const spine = createEffectSpine('fly')
    let bullet: Container
    if (spine !== null && hasAnimation(spine, EFFECT_ANIM.bullet)) {
      spine.state.setAnimation(0, EFFECT_ANIM.bullet, true)
      spine.scale.set(this.designScale)
      bullet = spine
    } else {
      if (spine !== null && !spine.destroyed) spine.destroy()
      const r = Math.max(6, 14 * this.designScale)
      bullet = new Graphics()
        .circle(0, 0, r)
        .fill({ color: 0xffe45c })
        .circle(0, 0, r)
        .stroke({ color: 0xff8a3c, width: Math.max(1, 2 * this.designScale) })
    }

    const dx = to.x - from.x
    const dy = to.y - from.y
    const distance = Math.hypot(dx, dy)
    // 對照 Unity BulletView：transform.right 朝行進方向（骨骼美術朝 +x）。
    bullet.rotation = Math.atan2(dy, dx)
    bullet.position.set(from.x, from.y)
    bullet.alpha = 0.6
    this.addChild(bullet)
    void tweenTo(bullet, { alpha: 1, duration: 0.1 })

    const speed = BULLET_SPEED_DESIGN * this.designScale
    const duration = Math.max(0.08, distance / Math.max(1, speed))
    await tweenTo(bullet, { x: to.x, y: to.y, duration, ease: 'power1.in' })
    killTweensOf(bullet)
    if (!bullet.destroyed) {
      this.removeChild(bullet)
      bullet.destroy()
    }
  }

  /**
   * 在指定螢幕座標播放一次性爆點特效後自動清除（非阻塞版由呼叫端 void 掉即可）。
   * 用於 retrigger「+N」爆點（ani_freespins `animation`，對照 Unity SpawnFreeSpinBullet 尾段
   * _freeSpinSpin 在 _freeSpinDummy＝SPIN 鈕上播放）等。骨骼不可用時靜默略過。
   */
  async playBurst(key: 'freespins' | 'reelGlow', animation: string, at: Point): Promise<void> {
    // 兩者皆為非首屏資產（見 main.ts 背景載入）；未就緒時下方會直接 return（略過特效）。
    await loadDeferredEffectSpines()
    const spine = createEffectSpine(key)
    if (spine === null || !hasAnimation(spine, animation)) {
      if (spine !== null && !spine.destroyed) spine.destroy()
      return
    }
    spine.scale.set(this.designScale)
    spine.position.set(at.x, at.y)
    this.addChild(spine)
    spine.state.setAnimation(0, animation, false)
    await delay(animationDuration(spine, animation, 0.8) * 1000)
    if (!spine.destroyed) {
      this.removeChild(spine)
      spine.destroy()
    }
  }

  /**
   * FG 進場盤面金光（ani_reel_glow，動畫名 L/P 依方向；對照 Unity ToFg 的 _reelGlowSpine 播一次）。
   * 置中全螢幕（Unity ReelGlow 節點掛 GameCanvas 中央）。
   */
  async playReelGlow(): Promise<void> {
    const animation =
      this.orientation === 'portrait' ? EFFECT_ANIM.reelGlowPortrait : EFFECT_ANIM.reelGlowLandscape
    await this.playBurst('reelGlow', animation, { x: this.screenW / 2, y: this.screenH / 2 })
  }

  /**
   * 免費遊戲等級過場（level 1~4 → lv_1_L/lv_2_L/lv_3_L/lv_max），置中全螢幕、播完自動清除。
   * 對照 Unity ShowLevel：await 過場動畫全長（se_pk_{level} 由 AudioManager 於 fgLevelIntroShown 播放）。
   */
  async playLevelTrans(level: number): Promise<void> {
    const names = this.orientation === 'portrait' ? EFFECT_ANIM.levelTransPortrait : EFFECT_ANIM.levelTrans
    const index = Math.max(1, Math.min(names.length, Math.floor(level))) - 1
    const animation = names[index]!
    // ani_trans 屬非首屏資產（見 main.ts 背景載入）；未就緒時下方走 600ms 停頓 fallback。
    await loadDeferredEffectSpines()
    const spine = createEffectSpine('trans')
    if (spine === null || !hasAnimation(spine, animation)) {
      if (spine !== null && !spine.destroyed) spine.destroy()
      // 骨骼不可用：短暫停頓保節奏（音效仍播）。
      await delay(600)
      return
    }
    spine.scale.set(this.designScale)
    spine.position.set(this.screenW / 2, this.screenH / 2)
    this.addChild(spine)
    spine.state.setAnimation(0, animation, false)
    await delay(animationDuration(spine, animation, 1.2) * 1000)
    if (!spine.destroyed) {
      this.removeChild(spine)
      spine.destroy()
    }
  }
}
